import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import ShowFinderWizard from '../components/ShowFinderWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShowFinderContext = createContext(null);

export const useShowFinder = () => {
  const ctx = useContext(ShowFinderContext);
  if (!ctx) throw new Error('useShowFinder must be used within ShowFinderProvider');
  return ctx;
};

export const ShowFinderProvider = ({ children, user, setUser }) => {
  const [status, setStatus] = useState(null); // { completed, dismissed } | null
  const [wizardOpen, setWizardOpen] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!user) {
      setStatus(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/show-finder/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      // 401s on logout are expected; swallow silently
    }
  }, [user]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // Auto-launch the wizard right after the product tour finishes
  // (only if the user hasn't already completed or dismissed it)
  useEffect(() => {
    const handler = () => {
      // Slight delay so the tour-end animation finishes first
      setTimeout(() => {
        setStatus((current) => {
          if (current && !current.completed && !current.dismissed) {
            setWizardOpen(true);
          }
          return current;
        });
      }, 400);
    };
    window.addEventListener('baadtv:tour-ended', handler);
    return () => window.removeEventListener('baadtv:tour-ended', handler);
  }, []);

  const openWizard = () => setWizardOpen(true);
  const closeWizard = () => { setWizardOpen(false); refreshStatus(); };

  const dismiss = async () => {
    try {
      await axios.post(`${API}/show-finder/dismiss`, {}, { withCredentials: true });
      setStatus((s) => ({ ...(s || {}), dismissed: true }));
    } catch (e) {
      console.error('Failed to dismiss show finder:', e);
    }
  };

  const reset = async () => {
    try {
      await axios.post(`${API}/show-finder/reset`, {}, { withCredentials: true });
      setStatus({ completed: false, dismissed: false });
    } catch (e) {
      console.error('Failed to reset show finder:', e);
    }
  };

  return (
    <ShowFinderContext.Provider value={{ status, openWizard, closeWizard, dismiss, reset, refreshStatus }}>
      {children}
      <ShowFinderWizard
        open={wizardOpen}
        onClose={closeWizard}
        onCompleted={refreshStatus}
        user={user}
        onUserUpdated={(updated) => setUser && setUser(updated)}
      />
    </ShowFinderContext.Provider>
  );
};

export default ShowFinderContext;
